/// <reference path="DocumentExtractionLineCell.ts" />
/// <reference path="InvoiceExtractedLineCell.ts" />
/// <reference path="DocumentExtractionInfoViewController.ts" />

// The extraction of a supplier document, shown inside the document's own detail view: a block
// above the lines with what was read (header, supplier match, warnings), a running check of
// the document total against the total read, and one row per detected line.
//
// On purchase invoices the rows are pre-filled add-line rows of the invoice grid
// (InvoiceExtractedLineCell): the concept carries the printed text, red until it is linked to
// a product or a concept item, and accepting a row creates the line exactly as typing it in the
// editor would. On supplier notes the rows are the compact proposal cells.
//
// The outcome goes back to DLInsightsBotServer, which learns the picks. Every document is confirmed
// here before anything is created. The host detail controller only provides the outlets
// (extraction-*) and two callbacks.

interface DocumentExtractionSessionDelegate
{
    extractionSessionDidInsertLine(session:DocumentExtractionSession, line:any) : void;
    extractionSessionDidFinish(session:DocumentExtractionSession) : void;
    /// The reading or its state changed (loaded, re-read, rows added).
    extractionSessionDidChange?(session:DocumentExtractionSession) : void;
}

class DocumentExtractionSession implements DocumentExtractionLineCellDelegate, InvoiceLineDelegate
{
    static attach(host:MUIViewController, document:DBCoreDocument, delegate:DocumentExtractionSessionDelegate) : DocumentExtractionSession {
        let tableView = MUIOutlet(host, "extraction-table-view", "UITableView") as UITableView;
        if (tableView == null) return null;
        let session = new DocumentExtractionSession();
        session.host = host;
        session.delegate = delegate;
        session.tableView = tableView;
        session.tableView.dataSource = session;
        session.blockView = (MUIOutlet(host, "extraction-view", "MUIView") as MUIView) ?? tableView;
        session.headerLabel = MUIOutlet(host, "extraction-header-lbl", "MUILabel");
        session.supplierLabel = MUIOutlet(host, "extraction-supplier-lbl", "MUILabel");
        session.totalsLabel = MUIOutlet(host, "extraction-totals-lbl", "MUILabel");
        session.warningsLabel = MUIOutlet(host, "extraction-warnings-lbl", "MUILabel");
        session.summaryLabel = MUIOutlet(host, "extraction-summary-lbl", "MUILabel");
        session.infoButton = MUIOutlet(host, "extraction-info-btn", "MUIButton");
        session.addAllButton = MUIOutlet(host, "extraction-add-all-btn", "MUIButton");
        session.rematchButton = MUIOutlet(host, "extraction-rematch-btn", "MUIButton");
        session.finishButton = MUIOutlet(host, "extraction-finish-btn", "MUIButton");
        if (session.infoButton) session.infoButton.setAction(session, function(this:DocumentExtractionSession){ this.showDetectionInfo(); });
        if (session.addAllButton) session.addAllButton.setAction(session, function(this:DocumentExtractionSession){ this.addAllLinked(); });
        if (session.rematchButton) session.rematchButton.setAction(session, function(this:DocumentExtractionSession){ this.rematch(); });
        if (session.finishButton) session.finishButton.setAction(session, function(this:DocumentExtractionSession){ this.finish(); });
        session.setDocument(document);
        return session;
    }

    host:MUIViewController = null;
    delegate:DocumentExtractionSessionDelegate = null;
    /// The detected row an invoice cell is turning into a line (set around the editor's accept).
    insertingRow:DocumentExtractionReviewRow = null;

    private blockView:MUIView = null;
    private headerLabel:MUILabel = null;
    private supplierLabel:MUILabel = null;
    private totalsLabel:MUILabel = null;
    private warningsLabel:MUILabel = null;
    private summaryLabel:MUILabel = null;
    private infoButton:MUIButton = null;
    private addAllButton:MUIButton = null;
    private rematchButton:MUIButton = null;
    private finishButton:MUIButton = null;
    private tableView:UITableView = null;

    private _document:DBCoreDocument = null;
    private _entityName:string = null;
    private record:any = null;
    private extraction:any = null;
    private proposal:any = null;
    private rows:DocumentExtractionReviewRow[] = [];        // rows still to handle
    private handled:DocumentExtractionReviewRow[] = [];     // added or marked, kept for the review
    private grouped:DocumentExtractionReviewRow[] = [];     // rows standing behind a custom line still to add
    /// The reading options of the document's legal entity, as the server keeps them.
    private options:any = null;
    private products:{[id:string]:Product} = {};
    private inputFormats:{[id:string]:StockInputFormat} = {};
    private taxes:{[id:string]:Tax} = {};
    private conceptItems:{[id:string]:ConceptItem} = {};
    private venueTaxes:Tax[] = null;
    private batching = false;

    get isInvoice() : boolean { return this._document instanceof PurchaseInvoice; }

    setDocument(document:DBCoreDocument){
        this._document = document;
        this._entityName = document instanceof StockNote ? "SupplierNote" : "PurchaseInvoice";
        this.rows = [];
        this.handled = [];
        this.grouped = [];
        this.options = null;
        this.finishing = false;
        this.finishOnLoad = false;
        this._state = "none";
        this._ocrError = null;
        this.setVisible(false, false);
        if (document == null) return;
        let status = document.ocrStatus;
        if (status != DBDocumentOCRStatus.extracted && status != DBDocumentOCRStatus.scanning && status != DBDocumentOCRStatus.failed) return;
        // a processed invoice takes no more lines: the review ends with what was added
        this.finishOnLoad = status == DBDocumentOCRStatus.extracted && document instanceof PurchaseInvoice && document.status == DBDocumentStatus.processed;
        this.load();
    }

    /// none | scanning | extracted | empty (a reading without lines) | failed, as the server says.
    private _state:string = "none";
    get state() : string { return this._state; }
    private _ocrError:string = null;
    get ocrError() : string { return this._ocrError; }
    /// Called when the state or the reading changes, for a view that shows them.
    stateDidChange:() => void = null;

    private finishOnLoad = false;
    private finishing = false;
    private rescanning = false;
    get isRescanning() : boolean { return this.rescanning; }
    private watching = false;
    private carryOver:DocumentExtractionReviewRow[] = [];

    // Reads the document again (a new engine run on the same files), then shows the new
    // reading when it arrives. The rows already added in this session stay added: they are
    // matched to the new lines by their printed text. The outcome goes to the completion.
    rescan(completion:(ok:boolean, message:string) => void){
        if (this.rescanning) return;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let document = this._document;
        this.rescanning = true;
        ad.webService.runDocumentExtraction(this._entityName, document.identifier, (code:number, data:any) => {
            if (this._document !== document) { this.rescanning = false; return; }
            if (code != 200) {
                this.rescanning = false;
                completion(false, MIOLocalizeString("COULDNT_START_THE_EXTRACTION", "Couldn't start the extraction."));
                return;
            }
            if (document instanceof PurchaseInvoice) {
                // the invoice list shows the processing screen while the document is read
                this.rescanning = false;
                DocumentExtractionSession.showInvoiceAfterRefresh(document.identifier);
                completion(true, null);
                return;
            }
            this.carryOver = this.handled.filter(r => r.applied);
            this._state = "scanning";
            this.showStateOnly();
            this.notifyChange();
            this.pollStatus(document, 0, (status:number, result:any) => {
                this.rescanning = false;
                this.applyStatus(status, result);
                if (status == DBDocumentOCRStatus.extracted) completion(true, null);
                else if (status == DBDocumentOCRStatus.failed) completion(false, this._ocrError ?? MIOLocalizeString("THE_DOCUMENT_COULD_NOT_BE_READ", "The document could not be read."));
                else completion(false, MIOLocalizeString("THE_DOCUMENT_IS_STILL_BEING_READ", "The document is still being read. Open this view again in a minute."));
            });
        });
    }

    // Asks the server every 3 seconds until the document leaves scanning (or a limit is
    // reached, in which case the completion gets scanning).
    private pollStatus(document:DBCoreDocument, attempt:number, completion:(status:number, data:any) => void){
        if (this._document !== document) return;
        if (attempt > 60) { completion(DBDocumentOCRStatus.scanning, {}); return; }
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        setTimeout(() => {
            if (this._document !== document) return;
            ad.webService.documentExtractionStatus(this._entityName, document.identifier, (code:number, data:any) => {
                if (this._document !== document) return;
                let status = code == 200 && data != null && data["ocrStatus"] != null ? Number(data["ocrStatus"]) : null;
                if (status == null || status == DBDocumentOCRStatus.scanning) this.pollStatus(document, attempt + 1, completion);
                else completion(status, data);
            });
        }, 3000);
    }

    // The document is being read (by the changelog job, or a run started elsewhere): wait for
    // the reading and show it when it arrives.
    private watchScanning(document:DBCoreDocument){
        if (this.watching) return;
        this.watching = true;
        this.pollStatus(document, 0, (status:number, data:any) => {
            this.watching = false;
            if (status != DBDocumentOCRStatus.scanning) this.applyStatus(status, data);
        });
    }

    /// Takes the invoice again from the server (its reading state changed there) and has the
    /// list show it: the processing screen while it is read, the detail view with a reading.
    static showInvoiceAfterRefresh(identifier:string){
        DBHelper.queryObjectWithCompletion("PurchaseInvoice", null, MIOPredicate.predicateWithFormat("identifier = '" + identifier + "'"), [], null, function(object:PurchaseInvoice){
            MIONotificationCenter.defaultCenter().postNotification("ReloadAndShowPurchaseInvoiceNotification", identifier);
        });
    }

    private static normalizedText(text:any) : string { return String(text ?? "").toLowerCase().replace(/\s+/g, " ").trim(); }

    hide(){ this.setVisible(false, false); }

    private setVisible(rows:boolean, icon:boolean){
        this.blockView.hidden = !rows;
        if (this.infoButton) this.infoButton.hidden = !icon;
    }

    private notifyChange(){
        if (this.stateDidChange != null) this.stateDidChange();
        if (this.delegate != null && this.delegate.extractionSessionDidChange != null) this.delegate.extractionSessionDidChange(this);
    }

    /// The reading, its proposal and the reading's findings, for a view of them.
    get reading() : any { return this.extraction; }
    get readingProposal() : any { return this.proposal; }
    get readingGates() : any[] { return ((this.record != null ? this.record["gates"] : null) ?? []) as any[]; }
    get document() : DBCoreDocument { return this._document; }

    /// The total read on the document, when there is a reading.
    get readTotal() : number {
        if (this._state != "extracted" && this._state != "empty") return null;
        let totals = this.extraction != null ? (this.extraction["totals"] ?? {}) : {};
        let total = totals["total"];
        return total != null && !isNaN(Number(total)) ? Number(total) : null;
    }

    // MARK: loading

    // The server is the truth for the state: the copy of the document here can be behind.
    private load(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let document = this._document;
        ad.webService.documentExtractionStatus(this._entityName, document.identifier, (code:number, data:any) => {
            if (this._document !== document) return;
            if (code == 200 && data != null && data["ocrStatus"] != null) this.applyStatus(Number(data["ocrStatus"]), data);
            else if (document.ocrStatus == DBDocumentOCRStatus.extracted) this.applyStatus(DBDocumentOCRStatus.extracted, { "result": document.ocrResponseRaw });
            else this.applyStatus(document.ocrStatus, {});
        });
    }

    // What the server says: the reading when there is one, else the state (being read, failed).
    private applyStatus(status:number, data:any){
        this._ocrError = data != null && data["ocrError"] != null ? String(data["ocrError"]) : null;
        if (data != null && data["options"] != null) this.options = data["options"];
        if (status == DBDocumentOCRStatus.extracted && data != null && data["result"] != null) {
            this._state = "extracted";
            this.setRecord(data["result"]);
        }
        else if (status == DBDocumentOCRStatus.scanning) {
            this._state = "scanning";
            this.showStateOnly();
            if (!this.rescanning) this.watchScanning(this._document);
        }
        else if (status == DBDocumentOCRStatus.failed) {
            this._state = "failed";
            this.showStateOnly();
        }
        else {
            this._state = "none";
            this.setVisible(false, false);
        }
        this.notifyChange();
    }

    // Nothing to show yet (being read, or the reading failed): no rows, the icon stays so the
    // state and RE-SCAN can be reached.
    private showStateOnly(){
        this.record = {};
        this.extraction = { "lines": [] };
        this.proposal = null;
        this.rows = [];
        this.handled = [];
        this.grouped = [];
        this.tableView.reloadData();
        this.updateSummary();
        this.setVisible(false, false);
    }

    private setRecord(record:any){
        this.record = record ?? {};
        this.extraction = this.record["extraction"] ?? { "lines": [] };
        this.proposal = this.record["proposal"] ?? null;
        let lines = (this.extraction["lines"] ?? []) as any[];
        if (this.finishOnLoad) { this.finishOnLoad = false; this.finish(); return; }
        if (lines.length == 0) {
            // a reading without lines is no use: say so, with what the reading found
            this._state = "empty";
            this.rows = []; this.handled = []; this.grouped = [];
            this.tableView.reloadData();
            this.renderHeader();
            this.updateSummary();
            this.setVisible(false, true);
            return;
        }
        this._state = "extracted";
        this.setVisible(true, true);
        this.renderHeader();
        this.buildRows();
    }

    private renderHeader(){
        if (this.headerLabel) this.headerLabel.text = this.headerText();
        if (this.supplierLabel) this.supplierLabel.text = this.supplierText();
        this.updateTotalsCheck();
        let warnings = this.warningTexts();
        if (this.warningsLabel) {
            this.warningsLabel.text = warnings.slice(0, 3).join("  |  ") + (warnings.length > 3 ? "  (+" + (warnings.length - 3) + ")" : "");
            this.warningsLabel.hidden = warnings.length == 0;
        }
    }

    headerText() : string {
        let ex = this.extraction;
        let esc = DocumentExtractionLineCell.escape;
        let type = ex["documentType"] ?? "";
        return MIOLocalizeString("DETECTED_LINES", "DETECTED LINES") + (type ? " (" + esc(type).replace(/_/g, " ") + ")" : "") + (ex["number"] ? "  " + esc(ex["number"]) : "") + (ex["date"] ? "  " + esc(ex["date"]) : "");
    }

    supplierText() : string {
        let esc = DocumentExtractionLineCell.escape;
        let supplier = this.extraction["supplier"] ?? {};
        let match = this.proposal != null ? this.proposal["supplier"] : null;
        let text = esc(supplier["name"] ?? "") + (supplier["taxId"] ? " (" + esc(supplier["taxId"]) + ")" : "");
        if (match != null) {
            let status = match["status"];
            if (status == "matched_by_tax_id") text += " → " + esc(match["name"]);
            else if (status == "matched_by_name") text += " → " + esc(match["name"]) + " " + MIOLocalizeString("BY_NAME", "(by name)");
            else if (status == "from_record") text += " → " + esc(match["name"]) + " " + MIOLocalizeString("FROM_UPLOAD", "(from the upload)");
            else text += " → " + MIOLocalizeString("UNKNOWN_SUPPLIER", "unknown supplier");
        }
        return text;
    }

    /// "error" when the reading has blocking findings or no lines, "warning" when it only has
    /// things to look at, null when it is clean: the colour of the badge on the reading icon.
    get issueLevel() : string {
        if (this._state == "empty") return "error";
        if (this._state != "extracted") return null;
        let blocking = false, any = false;
        for (let g of (this.record["gates"] ?? []) as any[]) { if (g["severity"] == "info") continue; any = true; if (g["severity"] == "blocking") blocking = true; }
        if (this.proposal != null) for (let w of (this.proposal["warnings"] ?? []) as any[]) { if (w["severity"] == "info" || w["lineIndex"] != null) continue; any = true; if (w["severity"] == "blocking") blocking = true; }
        return blocking ? "error" : (any ? "warning" : null);
    }

    warningTexts() : string[] {
        let esc = DocumentExtractionLineCell.escape;
        let warnings:string[] = [];
        for (let g of (this.record["gates"] ?? []) as any[]) if (g["severity"] != "info") warnings.push(esc(g["message"]));
        if (this.proposal != null) for (let w of (this.proposal["warnings"] ?? []) as any[]) if (w["severity"] != "info" && w["lineIndex"] == null) warnings.push(esc(w["message"]));
        return warnings;
    }

    // The totals read on the document against what the document adds up to now: the check
    // that says whether the lines are complete and right. True when they match.
    private _totalsMatch:boolean = null;
    get totalsMatch() : boolean { return this._totalsMatch; }

    totalsText() : string {
        let totals = this.extraction["totals"] ?? {};
        let readTotal = totals["total"] != null ? Number(totals["total"]) : null;
        let readBase = totals["base"] != null ? Number(totals["base"]) : null;
        let doc = this._document as any;
        let currentTotal = doc != null && doc.totalAmount != null ? Number(doc.totalAmount) : null;
        let currentBase = doc != null && doc.baseAmount != null ? Number(doc.baseAmount) : null;

        this._totalsMatch = null;
        let text = MIOLocalizeString("READ_ON_THE_DOCUMENT", "READ ON THE DOCUMENT") + ": " + MIOLocalizeString("BASE", "BASE") + " " + (readBase != null ? readBase.toFixed(2) : "-") + "  " + MIOLocalizeString("TOTAL", "TOTAL") + " " + (readTotal != null ? readTotal.toFixed(2) : "-");
        if (currentTotal != null) {
            text += "   |   " + MIOLocalizeString("DOCUMENT_NOW", "DOCUMENT NOW") + ": " + MIOLocalizeString("BASE", "BASE") + " " + (currentBase != null ? currentBase.toFixed(2) : "-") + "  " + MIOLocalizeString("TOTAL", "TOTAL") + " " + currentTotal.toFixed(2);
            if (readTotal != null) {
                let diff = currentTotal - readTotal;
                this._totalsMatch = Math.abs(diff) < 0.015;
                text += "   " + (this._totalsMatch ? MIOLocalizeString("TOTAL_MATCHES", "total matches") : MIOLocalizeString("DIFFERENCE", "difference") + " " + (diff > 0 ? "+" : "") + diff.toFixed(2));
            }
        }
        return text;
    }

    private updateTotalsCheck(){
        let text = this.totalsText();
        if (this.totalsLabel == null) return;
        this.totalsLabel.text = text;
        let layer = this.totalsLabel.layer as HTMLElement;
        layer.classList.remove("totals-ok", "totals-off");
        if (this._totalsMatch != null) layer.classList.add(this._totalsMatch ? "totals-ok" : "totals-off");
    }

    private buildRows(){
        let lines = (this.extraction["lines"] ?? []) as any[];
        let proposals = this.proposal != null ? (this.proposal["lines"] ?? []) as any[] : [];
        let productIDs:string[] = [], formatIDs:string[] = [], taxIDs:string[] = [], conceptIDs:string[] = [];
        for (let p of proposals) {
            if (p["productID"]) productIDs.push(p["productID"]);
            for (let c of (p["candidates"] ?? []) as any[]) if (c["productID"]) productIDs.push(c["productID"]);
            if (p["inputFormatID"]) formatIDs.push(p["inputFormatID"]);
            if (p["taxID"]) taxIDs.push(p["taxID"]);
            if (p["conceptItemID"]) conceptIDs.push(p["conceptItemID"]);
        }
        if (this.options != null && this.options["groupLinesConceptID"]) conceptIDs.push(this.options["groupLinesConceptID"]);
        this.grouped = [];
        this.rows = lines.map((line:any, i:number) => {
            let p = proposals.find((x:any) => x["lineIndex"] == i) ?? null;
            return { index: i, line: line, proposal: p, product: null, conceptItem: null, inputFormat: null, tax: null,
                     notAProduct: p != null && p["status"] == "not_a_product", skip: false, picked: false, applied: false } as DocumentExtractionReviewRow;
        });
        this.handled = [];
        this.tableView.reloadData();
        this.updateSummary();

        this.loadVenueTaxes(() => {
        this.fetchByIDs("Product", productIDs, ["tax", "taxBuy", "defaultInputFormat", "defaultWarehouse"], (objects:Product[]) => {
            for (let o of objects) this.products[o.identifier.toUpperCase()] = o;
            this.fetchByIDs("StockInputFormat", formatIDs, [], (formats:StockInputFormat[]) => {
                for (let f of formats) this.inputFormats[f.identifier.toUpperCase()] = f;
                this.fetchByIDs("Tax", taxIDs, [], (taxes:Tax[]) => {
                    for (let t of taxes) this.taxes[t.identifier.toUpperCase()] = t;
                    this.fetchByIDs("ConceptItem", conceptIDs, [], (concepts:ConceptItem[]) => {
                        for (let c of concepts) this.conceptItems[c.identifier.toUpperCase()] = c;
                        for (let row of this.rows) {
                            let p = row.proposal;
                            if (p == null) continue;
                            if (p["productID"]) row.product = this.products[String(p["productID"]).toUpperCase()] ?? null;
                            if (p["conceptItemID"]) row.conceptItem = this.conceptItems[String(p["conceptItemID"]).toUpperCase()] ?? null;
                            if (p["inputFormatID"]) row.inputFormat = this.inputFormats[String(p["inputFormatID"]).toUpperCase()] ?? null;
                            if (p["taxID"]) row.tax = this.taxes[String(p["taxID"]).toUpperCase()] ?? null;
                        }
                        for (let row of this.rows) row.taxUnmatched = row.line != null && row.line["taxRate"] != null && this.venueTaxForRate(row.line["taxRate"]) == null;
                        // after a re-scan: the rows added before stay added
                        for (let old of this.carryOver) {
                            let key = DocumentExtractionSession.normalizedText(old.line != null ? old.line["description"] : null);
                            let match = key.length > 0 ? this.rows.find(r => !r.applied && DocumentExtractionSession.normalizedText(r.line != null ? r.line["description"] : null) == key) : null;
                            if (match == null) continue;
                            match.applied = true;
                            match.product = old.product ?? match.product;
                            match.conceptItem = old.conceptItem ?? match.conceptItem;
                            this.rows = this.rows.filter(r => r !== match);
                            this.handled.push(match);
                        }
                        this.carryOver = [];
                        this.applyGroupOption();
                        this.tableView.reloadData();
                        this.updateSummary();
                        this.notifyChange();
                    });
                });
            });
        });
        });
    }

    private loadVenueTaxes(completion:any){
        if (this.venueTaxes != null) { completion(); return; }
        DBHelper.queryObjectsWithCompletion("Tax", null, MIOPredicate.predicateWithFormat("deletedAt = null"), [], this, function(this:DocumentExtractionSession, objects:Tax[]){
            this.venueTaxes = objects ?? [];
            completion();
        });
    }

    /// The venue's own tax with the rate printed on the document (21 -> IVA 21%), or null.
    venueTaxForRate(rate:any) : Tax {
        if (rate == null) return null;
        let wanted = Number(rate) / 100;
        if (isNaN(wanted)) return null;
        for (let t of this.venueTaxes ?? []) if (t.taxQuantity != null && Math.abs(t.taxQuantity - wanted) < 0.0001) return t;
        for (let id in this.taxes) if (Math.abs(this.taxes[id].taxQuantity - wanted) < 0.0001) return this.taxes[id];
        return null;
    }

    private fetchByIDs(entityName:string, ids:string[], relationships:string[], completion:any){
        let unique = ids.filter((id, i) => id != null && ids.indexOf(id) == i);
        if (unique.length == 0) { completion([]); return; }
        let pf = "identifier IN ['" + unique.join("','") + "']";
        DBHelper.queryObjectsWithCompletion(entityName, null, MIOPredicate.predicateWithFormat(pf), relationships, this, function(this:DocumentExtractionSession, objects:any[]){
            completion(objects ?? []);
        });
    }

    /// The venue tax for a rate read on the document, for the pre-filled rows.
    taxForRate(rate:any) : Tax {
        let tax = this.venueTaxForRate(rate);
        if (tax != null || rate == null) return tax;
        let wanted = Number(rate) / 100;
        let taxes = DBHelper.queryObjectsFromMainContext("Tax", null, MIOPredicate.predicateWithFormat("deletedAt = null"), []) as Tax[];
        for (let t of taxes ?? []) if (Math.abs(t.taxQuantity - wanted) < 0.0001) return t;
        return null;
    }

    // The totals as printed on the document: net base, discount, each tax with the venue tax
    // it matches (red when it is none of ours), the other amounts and the total.
    totalsDetailHTML() : string {
        let esc = DocumentExtractionLineCell.escape;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let cf = ad.currencyFormatter;
        let money = (v:any) => (v != null && !isNaN(Number(v))) ? cf.stringFromNumber(Number(v)) : "-";
        let present = (v:any) => v != null && !isNaN(Number(v)) && Number(v) != 0;
        let unmatched = '<span class="tax-unmatched">' + MIOLocalizeString("NOT_ONE_OF_OUR_TAXES", "(not one of our taxes)") + "</span>";
        let totals = this.extraction["totals"] ?? {};
        let lines:string[] = [];

        lines.push(MIOLocalizeString("NET_BASE", "NET (BASE)") + ": " + money(totals["base"]));
        if (present(totals["discount"])) lines.push(MIOLocalizeString("DISCOUNT", "DISCOUNT") + ": " + money(totals["discount"]));

        let summary = (this.extraction["taxSummary"] ?? []) as any[];
        for (let t of summary) {
            let tax = this.venueTaxForRate(t["rate"]);
            let label = "IVA " + esc(t["rate"] ?? "?") + "%";
            let text = (tax != null ? label : '<span class="tax-unmatched">' + label + "</span>") + ": " + MIOLocalizeString("BASE", "BASE") + " " + money(t["base"]) + "  " + MIOLocalizeString("TAX", "TAX") + " " + money(t["amount"]);
            text += tax != null ? "  (" + esc(tax.name) + ")" : "  " + unmatched;
            if (present(t["recargoAmount"]) || t["recargoRate"] != null) text += "   RE " + esc(t["recargoRate"] ?? "?") + "% " + money(t["recargoAmount"]);
            lines.push(text);
        }
        if (summary.length == 0 && totals["tax"] != null) {
            let base = Number(totals["base"]);
            let implied = base > 0 ? Math.round(Number(totals["tax"]) / base * 10000) / 100 : null;
            let tax = implied != null ? this.venueTaxForRate(implied) : null;
            let text = MIOLocalizeString("TAX", "TAX") + ": " + money(totals["tax"]);
            if (implied != null) text += "  (" + implied + "%" + (tax != null ? ", " + esc(tax.name) + ")" : ") " + unmatched);
            lines.push(text);
        }

        if (present(totals["recargo"])) lines.push("RE: " + money(totals["recargo"]));
        if (present(totals["withholding"])) lines.push(MIOLocalizeString("WITHHOLDING", "WITHHOLDING") + ": -" + money(totals["withholding"]));
        if (present(totals["shipping"])) lines.push(MIOLocalizeString("SHIPPING", "SHIPPING") + ": " + money(totals["shipping"]));
        if (present(totals["other"])) lines.push(MIOLocalizeString("OTHER_AMOUNTS", "OTHER AMOUNTS") + ": " + money(totals["other"]));
        lines.push("<strong>" + MIOLocalizeString("TOTAL", "TOTAL") + ": " + money(totals["total"]) + "</strong>");
        return lines.join("<br>");
    }

    private linkedCount() : number {
        return this.rows.filter(r => !r.notAProduct && (r.product != null || r.conceptItem != null || r.customName != null)).length;
    }

    summaryText() : string {
        let linked = 0, unresolved = 0, fees = 0;
        for (let r of this.rows) {
            if (r.notAProduct) fees++;
            else if (r.product != null || r.conceptItem != null || r.customName != null) linked++;
            else unresolved++;
        }
        let added = this.handled.filter(r => r.applied).length;
        return added + " " + MIOLocalizeString("ADDED", "added") + "  |  " + linked + " " + MIOLocalizeString("LINKED_READY_TO_ADD", "linked, ready to add") + "  |  " + unresolved + " " + MIOLocalizeString("TO_LINK", "to link") + "  |  " + fees + " " + MIOLocalizeString("NOT_PRODUCTS", "not products");
    }

    private updateSummary(){
        if (this.summaryLabel) this.summaryLabel.text = this.summaryText();
        if (this.addAllButton) this.addAllButton.enabled = this.linkedCount() > 0;
    }

    private rowsDidChange(){
        this.updateTotalsCheck();
        this.tableView.reloadData();
        this.updateSummary();
        // nothing left to decide: the review is over
        if (this.rows.length == 0 && this.handled.length > 0) this.finish();
    }

    // MARK: table

    numberOfSections(tableView:UITableView){ return 1; }

    numberOfRowsInSection(tableView:UITableView, section:number){ return this.rows.length; }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        let row = this.rows[indexPath.row];
        if (this.isInvoice) {
            let cell = tableView.dequeueReusableCellWithIdentifier("ExtractedLineCell") as InvoiceExtractedLineCell;
            cell.session = this;
            cell.invoice = this._document as Invoice;
            cell.delegate = this;
            cell.proposalRow = row;
            return cell;
        }
        let cell = tableView.dequeueReusableCellWithIdentifier("ExtractionLineCell") as DocumentExtractionLineCell;
        cell.delegate = this;
        cell.row = row;
        return cell;
    }

    // MARK: InvoiceLineDelegate (a pre-filled invoice cell created its line)

    lineDidInserted(line:InvoiceLine){
        let row = this.insertingRow;
        if (row != null) {
            row.applied = true;
            row.product = line.product ?? null;
            row.conceptItem = line.conceptItem ?? null;
            row.notAProduct = false;
            this.rows = this.rows.filter(r => r !== row);
            this.handled.push(row);
            if (row.group != null) {
                // the rows behind the custom line are booked to its concept, without teaching aliases
                for (let member of row.group) {
                    member.applied = true;
                    member.grouped = true;
                    member.product = null;
                    member.conceptItem = line.conceptItem ?? null;
                    member.notAProduct = false;
                    this.grouped = this.grouped.filter(r => r !== member);
                    this.handled.push(member);
                }
                if (row.remember && line.conceptItem != null) {
                    this.saveOptions({ "groupLines": true, "groupLinesConceptID": line.conceptItem.identifier.toUpperCase() }, null);
                }
            }
        }
        if (this.delegate != null) this.delegate.extractionSessionDidInsertLine(this, line);
        if (!this.batching) this.rowsDidChange();
    }

    lineDidRemove(line:InvoiceLine){ }
    lineDidUpdate(line:InvoiceLine){ }
    lineSectionDidInsert(line:DBDocumentTitleLine){ }

    // MARK: supplier note rows (DocumentExtractionLineCellDelegate)

    private pickingRow:DocumentExtractionReviewRow = null;

    pickProductForRow(cell:DocumentExtractionLineCell, row:DocumentExtractionReviewRow){
        if (row.applied) return;
        this.pickingRow = row;
        let search = row.line != null ? (row.line["description"] ?? "") : "";
        AppHelper.sharedInstance().showSelectStockProductViewControllerFromView(cell, search, this, this.productDidSelect, null, null);
    }

    private productDidSelect(controller:SelectEntityViewController, product:Product, supplierProduct?:SupplierProduct){
        if (this.pickingRow == null || product == null) return;
        this.pickingRow.product = product;
        this.pickingRow.picked = true;
        this.pickingRow.notAProduct = false;
        this.pickingRow.inputFormat = supplierProduct != null ? supplierProduct.inputFormat : product.defaultInputFormat;
        this.pickingRow.tax = this.pickingRow.tax ?? DBHelper.stockTaxFromProduct(product);
        this.products[product.identifier.toUpperCase()] = product;
        this.pickingRow = null;
        AppHelper.sharedInstance().dismissSelectStockProductViewController();
        this.tableView.reloadData();
        this.updateSummary();
    }

    toggleNotAProductForRow(row:DocumentExtractionReviewRow){
        if (row.applied) return;
        row.notAProduct = !row.notAProduct;
        this.tableView.reloadData();
        this.updateSummary();
    }

    addRow(row:DocumentExtractionReviewRow){
        if (row.applied || !(this._document instanceof StockNote)) return;
        let line = row.product != null ? this.createNoteLine(row, this._document as StockNote) : null;
        if (line == null) {
            AppHelper.showErrorMessage(this.host, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_LINE_NEEDS_A_PRODUCT_AND_A_QUANTITY", "The line needs a product and a quantity."));
            return;
        }
        row.applied = true;
        this.rows = this.rows.filter(r => r !== row);
        this.handled.push(row);
        DBHelper.saveMainContext();
        if (this.delegate != null) this.delegate.extractionSessionDidInsertLine(this, line);
        this.rowsDidChange();
    }

    // Adds every row already linked to a product or a concept: on invoices through their
    // pre-filled cells (the editor's own accept path, one line each), on notes through the
    // note line factory.
    private addAllLinked(){
        if (this.isInvoice) {
            let cells:InvoiceExtractedLineCell[] = [];
            for (let i = 0; i < this.rows.length; i++) {
                let row = this.rows[i];
                if (row.notAProduct || (row.product == null && row.conceptItem == null && row.customName == null)) continue;
                let cell = this.tableView.cellAtIndexPath(MIOIndexPath.indexForRowInSection(i, 0)) as InvoiceExtractedLineCell;
                if (cell != null && cell.proposalRow === row) cells.push(cell);
            }
            this.batching = true;
            for (let cell of cells) cell.enterDidPress();
            this.batching = false;
            this.rowsDidChange();
            return;
        }
        let inserted = 0;
        for (let row of this.rows.slice()) {
            if (row.notAProduct || row.product == null) continue;
            if (row.proposal == null || row.proposal["status"] != "matched") { if (!row.picked) continue; }
            let line = this.createNoteLine(row, this._document as StockNote);
            if (line == null) continue;
            row.applied = true;
            this.rows = this.rows.filter(r => r !== row);
            this.handled.push(row);
            inserted++;
            if (this.delegate != null) this.delegate.extractionSessionDidInsertLine(this, line);
        }
        if (inserted > 0) DBHelper.saveMainContext();
        this.rowsDidChange();
    }

    // A supplier note line through the note editor's own factory (DBHelper.createSupplierNoteLine).
    private createNoteLine(row:DocumentExtractionReviewRow, note:StockNote) : StockNoteLine {
        let line = row.line;
        let p = row.proposal ?? {};
        let product = row.product;
        let tax = row.tax ?? DBHelper.stockTaxFromProduct(product);

        let printedQuantity = Number(line["quantity"] ?? p["quantity"] ?? 0);
        let price = Number(line["unitPrice"] ?? 0);
        if (printedQuantity == 0) return null;

        let perPack = line["priceBasis"] == "per_pack" || (line["packQuantity"] != null && Number(line["packQuantity"]) > 1);
        let inputFormat = perPack ? (row.inputFormat ?? product.defaultInputFormat) : null;
        let inputType = inputFormat != null ? MeasureUnitType.InputFormat : product.measureType;
        let quantity = inputFormat != null ? printedQuantity : Number(p["productQuantity"] ?? printedQuantity);

        let values = DBHelper.calculateQuantityFromStockLine(quantity, price, inputFormat, inputType, product.measureType, product.containerMeasureType, product.containerQuantity);
        let productQuantity = values[0];
        let productPrice = values[1];

        let gross = quantity * price;
        let discount = line["discount"] ?? null;
        let discountFormat:string = null;
        let discountAmount = 0;
        if (discount != null && discount["percent"] != null) { discountFormat = discount["percent"] + "%"; discountAmount = gross * Number(discount["percent"]) / 100; }
        else if (discount != null && discount["amount"] != null) { discountFormat = String(discount["amount"]); discountAmount = Number(discount["amount"]); }
        let base = line["base"] != null ? Number(line["base"]) : gross - discountAmount;
        if (discountFormat == null && line["total"] != null && Number(line["total"]) < gross - 0.005) { discountAmount = gross - Number(line["total"]); discountFormat = discountAmount.toFixed(2); base = Number(line["total"]); }

        let warehouse = note.overrideWarehouse ?? product.defaultWarehouse ?? AppHelper.sharedInstance().defaultWarehouse;
        let noteLine = DBHelper.createSupplierNoteLine(product, tax, inputFormat, inputType, quantity, productQuantity, price, productPrice, base, discountFormat, discountAmount, warehouse, note);
        if (noteLine == null) return null;
        let minimum = MeasureUnits.convertToMinimumUnits(productQuantity, product.measureType, product.containerMeasureType, product.containerQuantity);
        noteLine.minMeasureQuantity = minimum[0];
        noteLine.minMeasureType = minimum[1];
        return noteLine;
    }

    // MARK: the recognised document, re-match and finish

    // The informative view behind the top-bar icon: what was read, what was proposed, and
    // where the review stands.
    private showDetectionInfo(){
        let vc = DocumentExtractionInfoViewController.newInstance();
        vc.session = this;
        this.host.presentViewController(vc, true);
    }

    /// Every detected row, pending or handled, in document order.
    get allRows() : DocumentExtractionReviewRow[] {
        return this.rows.concat(this.grouped).concat(this.handled).filter(r => r.group == null).sort((a, b) => a.index - b.index);
    }

    /// The reading options of the document's legal entity, as the server keeps them (null until loaded).
    get readingOptions() : any { return this.options; }

    /// The concept the supplier's custom line is booked to, when the option names one.
    get groupLinesConcept() : ConceptItem {
        let id = this.options != null ? this.options["groupLinesConceptID"] : null;
        return id ? (this.conceptItems[String(id).toUpperCase()] ?? null) : null;
    }

    // MARK: reading options

    // Writes the options on the server, which re-derives the reading with them: the rows are
    // rebuilt from it, the ones already added stay added.
    saveOptions(values:any, completion:(ok:boolean) => void){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let document = this._document;
        ad.webService.postDocumentExtractionOptions(this._entityName, document.identifier, values, (code:number, data:any) => {
            if (this._document !== document) return;
            if (code != 200 || data == null) { if (completion != null) completion(false); return; }
            if (data["options"] != null) this.options = data["options"];
            if (data["result"] != null) {
                this.carryOver = this.handled.filter(r => r.applied && r.group == null);
                this.setRecord(data["result"]);
            }
            this.refreshLegalEntityOptions();
            this.notifyChange();
            if (completion != null) completion(true);
        });
    }

    // The manager's own copy of the legal entity's options row, after the server wrote it.
    private refreshLegalEntityOptions(){
        let entity = (this._document as any).legalEntity ?? (this._document as any).originEntity ?? null;
        if (entity == null || entity.identifier == null) return;
        DBHelper.queryObjectWithCompletion("LegalEntity", null, MIOPredicate.predicateWithFormat("identifier = '" + entity.identifier + "'"), ["extractionOptions"], null, function(object:LegalEntity){ });
    }

    // MARK: custom lines (several detected rows booked as one concept line)

    /// Rows chosen for a custom line.
    get selectedCount() : number { return this.rows.filter(r => r.selected && r.group == null).length; }

    toggleSelection(row:DocumentExtractionReviewRow, on:boolean){
        if (row.applied || row.group != null) return;
        row.selected = on;
    }

    // From a row's options: that row and the rows picked with it become one concept line (one
    // per VAT rate read); the concept is typed, the supplier's one when it has one, and can be
    // kept as the supplier's option.
    createCustomLine(including:DocumentExtractionReviewRow){
        let selected = this.rows.filter(r => (r.selected || r === including) && r.group == null && !r.applied);
        if (selected.length == 0) return;
        let concept = this.groupLinesConcept;
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("CUSTOM_LINE", "CUSTOM LINE"), selected.length + " " + MIOLocalizeString("LINES_SELECTED", "lines selected"), MUIAlertViewStyle.Default);
        avc.addTextFieldWithConfigurationHandler(this, function(this:DocumentExtractionSession, textField:MUITextField){
            textField.setPlaceholderText(MIOLocalizeString("CONCEPT", "Concept"));
            textField.text = concept != null ? concept.name : this.defaultCustomLineName();
        });
        let create = (remember:boolean) => {
            let name = (avc.textFields[0].text ?? "").trim();
            if (name.length == 0) return;
            this.conceptForName(name, concept, (item:ConceptItem) => this.groupRows(selected, item, name, remember));
        };
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CREATE", "CREATE"), MUIAlertActionStyle.Default, this, function(this:DocumentExtractionSession){ create(false); }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CREATE_AND_REMEMBER_FOR_THE_SUPPLIER", "CREATE AND REMEMBER FOR THE SUPPLIER"), MUIAlertActionStyle.Default, this, function(this:DocumentExtractionSession){ create(true); }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null));
        if (this.host != null) this.host.presentViewController(avc, true);
    }

    private defaultCustomLineName() : string {
        let entity = (this._document as any).legalEntity ?? (this._document as any).originEntity ?? null;
        return entity != null && entity.name != null ? entity.name : "";
    }

    // The concept item for a typed name: the supplier's when the name is its own, else an
    // existing one with that name, else none (the editor creates one when the line is added).
    private conceptForName(name:string, known:ConceptItem, completion:(item:ConceptItem) => void){
        if (known != null && known.name == name) { completion(known); return; }
        let predicate = MIOPredicate.predicateWithFormat("name = '" + name.replace(/'/g, "\\'") + "' and deletedAt = null");
        DBHelper.queryObjectWithCompletion("ConceptItem", null, predicate, [], this, function(this:DocumentExtractionSession, object:ConceptItem){
            completion(object ?? null);
        });
    }

    // The supplier's option: everything read is grouped into its custom line as soon as the
    // rows exist, ready to accept.
    private applyGroupOption(){
        if (this.options == null || this.options["groupLines"] != true) return;
        if (this.rows.length == 0 || this.handled.length > 0 || this.grouped.length > 0) return;
        let concept = this.groupLinesConcept;
        this.groupRows(this.rows.slice(), concept, concept != null ? concept.name : this.defaultCustomLineName(), false);
    }

    // One custom line per VAT rate read among the rows, with the sum of their bases, in the
    // place of the rows; the rows wait behind it until the line is added.
    private groupRows(members:DocumentExtractionReviewRow[], concept:ConceptItem, name:string, remember:boolean){
        let pending = members.filter(r => !r.applied && r.group == null && r.grouped != true);
        if (pending.length == 0) return;
        let byRate:{[key:string]:DocumentExtractionReviewRow[]} = {};
        let keys:string[] = [];
        for (let r of pending) {
            let key = r.line != null && r.line["taxRate"] != null && !isNaN(Number(r.line["taxRate"])) ? String(Number(r.line["taxRate"])) : "";
            if (byRate[key] == null) { byRate[key] = []; keys.push(key); }
            byRate[key].push(r);
        }
        let customLines = this.rows.concat(this.handled).filter(r => r.group != null).length;
        let created:DocumentExtractionReviewRow[] = [];
        for (let key of keys) {
            let group = byRate[key];
            let base = 0, total = 0;
            for (let r of group) { base += DocumentExtractionSession.lineBase(r.line); total += DocumentExtractionSession.lineTotal(r.line); }
            base = Math.round(base * 100) / 100;
            total = Math.round(total * 100) / 100;
            let rate = key.length > 0 ? Number(key) : null;
            let texts = group.map(r => r.line != null ? String(r.line["description"] ?? "").trim() : "").filter(t => t.length > 0);
            let line = { "description": group.length + " " + MIOLocalizeString("LINES_GROUPED", "lines grouped") + (texts.length > 0 ? ": " + texts.join(" · ") : ""),
                         "quantity": 1, "unitPrice": base, "base": base, "taxRate": rate, "total": total };
            let row = { index: -1 - customLines - created.length, line: line, proposal: null, product: null, conceptItem: concept, inputFormat: null,
                        tax: rate != null ? this.taxForRate(rate) : null, notAProduct: false, skip: false, picked: false, applied: false,
                        group: group, customName: concept == null ? name : null, remember: remember } as DocumentExtractionReviewRow;
            created.push(row);
        }
        for (let r of pending) {
            r.selected = false;
            this.rows = this.rows.filter(x => x !== r);
            this.grouped.push(r);
        }
        this.rows = created.concat(this.rows);
        this.rowsDidChange();
    }

    /// The rows behind a custom line come back as rows of their own.
    ungroup(row:DocumentExtractionReviewRow){
        if (row.group == null || row.applied) return;
        for (let member of row.group) {
            this.grouped = this.grouped.filter(r => r !== member);
            if (!this.rows.some(r => r === member)) this.rows.push(member);
        }
        this.rows = this.rows.filter(r => r !== row).sort((a, b) => a.index - b.index);
        this.rowsDidChange();
    }

    /// The net base of a detected line as the review row takes it: the base printed, else the
    /// total, else quantity × price less the discount.
    static lineBase(line:any) : number {
        if (line == null) return 0;
        let printed = line["base"] != null ? Number(line["base"]) : (line["total"] != null ? Number(line["total"]) : null);
        if (printed != null && !isNaN(printed)) return printed;
        let quantity = Number(line["quantity"] ?? 1) || 1;
        let price = Number(line["unitPrice"] ?? 0) || 0;
        let gross = quantity * price;
        let discount = line["discount"] ?? null;
        if (discount != null && discount["percent"] != null) return gross - gross * Number(discount["percent"]) / 100;
        if (discount != null && discount["amount"] != null) return gross - (Number(discount["amount"]) || 0);
        return gross;
    }

    static lineTotal(line:any) : number {
        if (line == null) return 0;
        if (line["total"] != null && !isNaN(Number(line["total"]))) return Number(line["total"]);
        let base = DocumentExtractionSession.lineBase(line);
        let rate = line["taxRate"] != null ? Number(line["taxRate"]) : 0;
        return base * (1 + (isNaN(rate) ? 0 : rate) / 100);
    }

    /// The detected lines still to add to the document.
    get pendingRowCount() : number { return this.rows.length; }

    private rematch(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.webService.documentExtractionMatch(this._entityName, this._document.identifier, (code:number, data:any) => {
            if (code == 200 && data != null && data["proposal"] != null) {
                this.record["proposal"] = data["proposal"];
                this.setRecord(this.record);
            }
            else AppHelper.showErrorMessage(this.host, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("COULDNT_REMATCH", "Couldn't re-match the document."));
        });
    }

    // Sends the outcome (what was added and to which product or concept, what is not a product)
    // to the AI server, which learns the picks; the document leaves the extracted state.
    private finish(){
        if (this.finishing) return;
        this.finishing = true;
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let picks = this.handled.filter(r => r.group == null).map(r => {
            return { "lineIndex": r.index,
                     "productID": (r.notAProduct || r.product == null) ? null : r.product.identifier.toUpperCase(),
                     "conceptItemID": (r.notAProduct || r.product != null || r.conceptItem == null) ? null : r.conceptItem.identifier.toUpperCase(),
                     "inputFormatID": (r.inputFormat != null && !r.notAProduct) ? r.inputFormat.identifier.toUpperCase() : null,
                     "notAProduct": r.notAProduct,
                     "learn": r.grouped != true };
        });
        let added = this.handled.filter(r => r.applied).length;
        let outcome = { "by": ad.selectedUser != null ? (ad.selectedUser as any).name : null,
                        "supplierID": this.proposal != null && this.proposal["supplier"] != null ? this.proposal["supplier"]["supplierID"] : null,
                        "changed": false,
                        "corrections": [],
                        "picks": picks,
                        "applied": added > 0 };
        let document = this._document;
        ad.webService.postDocumentExtractionReview(this._entityName, document.identifier, outcome, (code:number, data:any) => {
            if (code != 200) {
                this.finishing = false;
                AppHelper.showErrorMessage(this.host, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_REVIEW_COULD_NOT_BE_SAVED", "The review could not be saved."));
                return;
            }
            document.ocrStatus = DBDocumentOCRStatus.processed;
            DBHelper.saveMainContext();
            this.hide();
            if (this.delegate != null) this.delegate.extractionSessionDidFinish(this);
        });
    }
}
